import {
    takeLatest,
    put,
    call,
    select,
    take,
    fork,
    cancel,
    actionChannel,
    race,
    delay,
    takeMaybe,
} from 'redux-saga/effects';
import noop from 'lodash/noop';
import get from 'lodash/get';
import { sha256 } from 'js-sha256';
import invoke from 'lodash/invoke';
import once from 'lodash/once';
import { loginAccount, savedItemsMerge, logout } from 'yoda-interfaces/lib/Account/AccountApi';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import TokenProvider, {
    setValue,
} from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import CoreConstants from 'yoda-core-components/lib/common/Constants';
import setUserName from 'yoda-account-components/lib/common/setUserName';
import { RESET_QUICKSCREEN_ELIGIBLITY } from 'yoda-account-components/lib/actionTypes/SYFActionTypes';
import { signinSuccessAction } from '../actions/SignInAction';
import {
    signInSliderMounted,
    initiateSignInSliderClose,
    hideWelcomeToolTip,
    resetSignInSlider,
} from '../actions/SignInSliderActions';
import { accountRegisteredAction } from '../actions/CreateAccountActions';
import { getRewards, rewardsMigrationAction } from '../actions/RewardsAction';
import * as SignInActionTypes from '../actionTypes/SigninActionTypes';
import * as SignInSliderActionTypes from '../actionTypes/SignInSliderActionTypes';
import * as CreateAccountActionTypes from '../actionTypes/CreateAccountActionTypes';
import { SAVEDITEMS_GET_REQUEST } from '../actionTypes/SavedItemActionTypes';
import { startPhoneVerification } from '../actions/PhoneVerificationAction';
import { getAccertifyDeviceInfo, logExtendedAnalytics } from './SiteProbingSaga';
import {
    PLCC_BANNER_REQUEST,
    CLEAR_PLCC_BANNER_DETAILS,
} from '../actionTypes/PLCCBannerActionTypes';
import User from '../helpers/User/User';
import { isPDPPage } from '../helpers/Utils/pageType';
import { selectFeatureFlags, selectPreferences } from '../selectors/ContextSelector';
import {
    IOVATION_SCRIPT_LOAD_ERROR,
    IOVATION_SCRIPT_LOAD_SUCCESS,
} from '../actionTypes/KeepMeLoggedInActionTypes';
import { GLOBAL_LOGOUT } from '../actionTypes/GlobalLogoutActionTypes';
import siteItemCountAction from '../actions/ItemCountAction';
import siteStoreActions from '../actions/SetMyDefaultStoreAction';
import { getUserDetails } from '../actions/AccountAction';
import { REFRESH_TOKEN, DP_HAS_JCP_CARD } from '../common/Constants';
import { selectKeepMeLoggedInThrottleFF } from '../selectors/KeepMeLoggedInSelector';
import { selectPlccBannerThrottleFF } from '../selectors/plccBannerSelector';
import { rewardsSaga } from './RewardsSaga';

export const DEFAULTS = {
    signInSliderDetails: {},
};
const { DP_KEEP_ME_LOGGED } = CoreConstants;

export const getSignInSliderDetails = (state) =>
    state.signInSliderDetails || DEFAULTS.signInSliderDetails;

export function* updateHeaderReduxState() {
    try {
        const preferences = yield select(selectPreferences);
        yield put(getUserDetails());
        yield put(siteItemCountAction.updateItemCountAction());
        if (!preferences.storeCallNotNeededOnPageLoad) {
            yield put(siteStoreActions.setMyDefaultStore());
        }
    } catch (error) {
        console.log('updateHeaderReduxState: ', error);
    }
}

export function* storeSignInSuccessInfo({ response, savedItemMergePayload, source, email } = {}) {
    TokenProvider.set('DP_USER_EMAIL', email);
    TokenProvider.set('DP_USER_EMAIL_HASHED', email ? sha256(email) : '');
    TokenProvider.storeTokens(response, 'body', true);
    setUserName('DP_USER_NAME');
    setUserName('DP_USER_INFO');
    setUserName('USER_ACCT_ID');
    setUserName('DYN_USER_ID');
    TokenProvider.set('DP_FULL_ACCESS_EXPIRY', get(response, 'data.expires_in', '').toString());
    const isRecognisedState = TokenProvider.get('DP_USER_STATE') === '2';
    if (TokenProvider.get('DP_SAVED_ITEMS_PAYLOAD') && !isRecognisedState) {
        const data = JSON.parse(TokenProvider.get('DP_SAVED_ITEMS_PAYLOAD'));
        // added post sign in success after merge call
        yield put({
            type: SignInActionTypes.SAVED_ITEMS_MERGE,
            source,
            data,
            isSignInSliderReq: get(savedItemMergePayload, 'isSignInSliderReq'),
            payloadData: get(savedItemMergePayload, 'payload'),
            signIn: get(savedItemMergePayload, 'signIn'),
        });
        LocalStorage.removeData('DP_SAVED_ITEMS_PAYLOAD');
    }
    yield call(updateHeaderReduxState);
}

export function* welcomePageCallBack(postCreateAccountRedirect, postAuthenticationUrl) {
    try {
        yield put(initiateSignInSliderClose());
        if (postCreateAccountRedirect) {
            window.location.assign(postCreateAccountRedirect);
            return;
        }
        if (postAuthenticationUrl) {
            window.location.assign(postAuthenticationUrl);
        }
    } catch (error) {
        console.log('welcomePageCallBack error: ', error);
    }
}

export function* signInSaga(action) {
    try {
        action.payload.grant_type = 'password';
        action.payload.guestAccountId = TokenProvider.get('ACCOUNT_ID');
        const { isPhone } = action.payload;
        delete action.payload.featureFlags;
        delete action.payload.isPhone;
        const { keepMeLoggedIn } = action.payload;
        const { rememberMe } = action.payload;
        const { isSignInSliderReq } = action.payload;
        delete action.payload.isSignInSliderReq;
        delete action.payload.rememberMe;
        delete action.payload.keepMeLoggedIn;
        console.log('SIGNINSaga external site');
        const isRecognisedState = TokenProvider.get('DP_USER_STATE') === '2';
        const { enableADISignin = true, enableQuickScreenBanner = true } = yield select(
            selectFeatureFlags
        ) || {};
        if (keepMeLoggedIn) {
            const bbId = !__SERVER__ && get(invoke(window, 'IGLOO.getBlackbox'), 'blackbox');
            const customHeaders = {
                ...(bbId && { 'X-BLACKBOX': bbId }),
                'X-KEEP-ME-SIGNED': keepMeLoggedIn,
            };
            delete action.payload.keepMeLoggedIn;
            action.payload.customHeaders = customHeaders;
        }
        if (enableADISignin) {
            const deviceInfo = yield call(getAccertifyDeviceInfo, {
                actionName: isSignInSliderReq ? 'slider_signin' : 'cam_signin',
            });
            if (deviceInfo) {
                action.payload.deviceInfo = deviceInfo;
            }
        }

        const response = yield call(loginAccount, action.payload);
        const payload = {
            response: response.data,
            isPhone,
            isSliderCustomized: action.payload.isSliderCustomized, // Required while executing onAuthenticationSuccess
        };
        /** trigger ADI Success event for signin page not reload */
        const adiStatus = response?.headers?.get('x-adi-sts');

        let setADIValueToLS = false;
        if (!isSignInSliderReq && response.status === 200) {
            setADIValueToLS = true;
        }
        if (enableADISignin && ['A', 'R'].includes(adiStatus)) {
            yield call(logExtendedAnalytics, {
                result: { response },
                isTriggerFromLS: setADIValueToLS,
                actionMethod: isSignInSliderReq ? 'SLIDER_SIGNIN_ACCOUNT' : 'CAM_SIGNIN_ACCOUNT',
            });
        }

        if (response.status === 200) {
            response.userState = '1';
            if (rememberMe) {
                TokenProvider.set('DP_USER_REMEMBER', '1');
            } else {
                TokenProvider.set('DP_USER_REMEMBER', '0');
            }
            /* B2B Voucher Portal code */
            // adding into local storage
            const checkAccountType = TokenProvider.get('ACCOUNT_TYPE', true);
            if (checkAccountType === 'B2B_VOUCHER') {
                TokenProvider.set('ACCOUNT_TYPE', 'B2B_VOUCHER');
            }
            /// ////////////////////////////
            // TokenProvider.storeTokens(response, 'body', true);
            payload.isSuccess = true;
            // setUserName('DP_USER_NAME');
            // setUserName('DP_USER_INFO');
            // setUserName('USER_ACCT_ID');
            // setUserName('DYN_USER_ID');
            // TokenProvider.set('DP_FULL_ACCESS_EXPIRY', response.data.expires_in.toString());
            const savedItemMergePayload = {
                payload,
                isSignInSliderReq,
                signIn: true,
            };
            yield fork(storeSignInSuccessInfo, {
                response,
                savedItemMergePayload,
                source: 'signin',
                email: get(action, 'payload.email', ''),
            });
            yield call(rewardsSaga, getRewards);
            if (isSignInSliderReq) {
                if (
                    enableQuickScreenBanner &&
                    !__SERVER__ &&
                    (window?.location?.pathname?.indexOf('/account/dashboard') !== -1 ||
                        isPDPPage(window?.location?.pathname))
                ) {
                    payload.isQuickScreenEligible = true;
                }
                yield put({
                    type: SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS,
                    payload,
                });
            } else {
                yield put({ type: SignInActionTypes.POST_SIGNIN_REQUEST_SUCCESS, payload });
            }
            yield put(signinSuccessAction(isPhone, rememberMe, keepMeLoggedIn));
            // MCYODA-29273 | Single click checkout not working when session expires and user login in warm state in cart page.
            if (
                isRecognisedState &&
                !__SERVER__ &&
                window?.location?.pathname?.indexOf('/cart') !== -1
            ) {
                yield fork(welcomePageCallBack, '', window.location.href);
            }
            // SIGN IN SLIDER
        } else {
            response.userState = '';
            payload.isSuccess = false;
            const isMonitoringMode = response?.headers?.get('x-adi-monitor');
            payload.isAdiReject = enableADISignin && !isMonitoringMode && adiStatus === 'R';
            if (isSignInSliderReq) {
                yield put({
                    type: SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_ERROR,
                    payload,
                });
            } else {
                yield put({ type: SignInActionTypes.POST_SIGNIN_REQUEST_ERROR, payload });
            }
        }
    } catch (error) {
        console.log('Post signIn saga error: ', error);
    }
}

export function* savedItemMerge(action) {
    try {
        /* This code is added here to handle site-components Cyclic Dependency */
        const accessToken = TokenProvider.get('Access_Token');
        const accountId = TokenProvider.get('ACCOUNT_ID');
        const postData = { data: action.data, accessToken, accountId };
        yield call(savedItemsMerge, postData);
        yield put({ type: SAVEDITEMS_GET_REQUEST, forceGetSavedItems: true });
        if (action.source === 'signin' || action.source === 'createaccount') {
            if (action.isSignInSliderReq) {
                if (!action.signIn) {
                    action.payloadData.isSliderCustomized = false; // Required while executing onAuthenticationSuccess
                }
                yield put({
                    type: !action.signIn
                        ? SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS
                        : SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS,
                    payload: action.payloadData,
                });
            } else {
                yield put({
                    type: !action.signIn
                        ? CreateAccountActionTypes.CREATE_ACCOUNT_SUCCESS
                        : SignInActionTypes.POST_SIGNIN_REQUEST_SUCCESS,
                    payload: action.payloadData,
                });
            }
        }
    } catch (error) {
        console.log(error);
    }
}

export function* customStepUpSaga(action) {
    try {
        // to check if step up required
        // otherwise execute onSucess callback
        const {
            payload: {
                customizeConfig: {
                    openCreateAccount,
                    forceLogin,
                    ensureRestricted,
                    openLogoutSlider,
                    skipOnAuthenticationSuccess,
                    postAuthenticationUrl,
                    lnlEnrollPhoneSlider,
                    lnlPage,
                    isHideAccountMenu,
                } = {},
                customCallbacks: { onAuthentication = noop, onClose = noop, onSkip = noop } = {},
            } = {},
        } = action;

        // this means no stepup required for already strict logged user (state '1')
        // user state 2
        // when forceLogin : true, no matter userstate your data is restricted
        // when !ensureRestricted , that means you just need to be loggedin
        // when ensureRestricted, that means its stepUp for recognized state
        if (
            !forceLogin &&
            ((!ensureRestricted && User.isUserLoggedIn()) ||
                (ensureRestricted && User.isUserLoggedIn(true)))
        ) {
            yield* onAuthentication();
            yield put({ type: SignInSliderActionTypes.CUSTOM_STEPUP_ALREADY_AUTHENTICATED });
            return;
        }

        // used same flag setting in this action type
        yield put({
            type: SignInSliderActionTypes.SIGNIN_SLIDER_OPEN,
            payload: {
                openCreateAccount,
                openLogoutSlider,
                isSliderCustomized: true,
                postAuthenticationUrl,
                skipOnAuthenticationSuccess,
                lnlEnrollPhoneSlider,
                lnlPage,
                isHideAccountMenu,
            },
        });

        const { type } = yield take([
            SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS,
            SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS,
            SignInSliderActionTypes.UNMOUNT_SIGNIN_SLIDER,
            SignInSliderActionTypes.CUSTOM_STEPUP_SIGNIN_SLIDER_SKIP,
        ]);
        switch (type) {
            case SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS:
            case SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS:
                if (!skipOnAuthenticationSuccess) {
                    yield* onAuthentication();
                }
                break;

            case SignInSliderActionTypes.UNMOUNT_SIGNIN_SLIDER:
                yield* onClose();
                break;

            case SignInSliderActionTypes.CUSTOM_STEPUP_SIGNIN_SLIDER_SKIP:
                yield* onSkip();
                break;

            default:
                console.error('missing implementation', type);
        }
    } catch (error) {
        console.error('customStepUpSaga', error);
    }
}

function* getPostCreateAccountRedirect() {
    try {
        const signInsliderDetails = yield select(getSignInSliderDetails);
        const { openCreateAccount } = signInsliderDetails;
        const featureFlags = yield select(selectFeatureFlags);
        const preferences = yield select(selectPreferences);
        const currentPage =
            !__SERVER__ && window.location.pathname ? window.location.pathname : undefined;
        let postCreateAccountRedirect;
        const signInSliderDetails = yield select(getSignInSliderDetails);
        const { postAuthenticationUrl } = signInSliderDetails;
        if (
            openCreateAccount &&
            postAuthenticationUrl === undefined &&
            featureFlags.enableCreateAccountRedirects &&
            get(preferences, `createAccountRedirects[${currentPage}]`)
        ) {
            postCreateAccountRedirect = preferences.createAccountRedirects[currentPage];
        }
        return postCreateAccountRedirect;
    } catch (error) {
        console.log('getPostCreateAccountRedirect error: ', error);
        return '';
    }
}

function* getPostAuthenticationUrl() {
    try {
        const featureFlags = yield select(selectFeatureFlags);
        const preferences = yield select(selectPreferences);
        const currentPage =
            !__SERVER__ && window.location.pathname ? window.location.pathname : undefined;
        const signInSliderDetails = yield select(getSignInSliderDetails);
        let { postAuthenticationUrl } = signInSliderDetails;
        if (
            featureFlags.enableSignInSliderAuthRedirects &&
            postAuthenticationUrl === undefined &&
            get(preferences, `authenticationSuccessRedirects[${currentPage}]`) === undefined
        ) {
            postAuthenticationUrl = preferences.signInSliderAuthRedirects[currentPage];
        }
        return postAuthenticationUrl;
    } catch (error) {
        console.log('getPostCreateAccountRedirect error: ', error);
        return '';
    }
}

function* callWelcomePageCallbackAfterDelay(postCreateAccountRedirect, postAuthenticationUrl) {
    try {
        // get time from config
        const preferences = yield select(selectPreferences);
        const signInSliderWelcomeMessageTimeOut = get(
            preferences,
            'signInSliderWelcomeMessageTimeOut',
            3000
        );
        const parsedNumber = Number(signInSliderWelcomeMessageTimeOut);
        const DELAY = parsedNumber || 3000;
        yield delay(DELAY);
        yield fork(welcomePageCallBack, postCreateAccountRedirect, postAuthenticationUrl);
    } catch (err) {
        console.error('some error occured:', err);
    }
}

function* delayedCallWelcomePageTask(postCreateAccountRedirect, postAuthenticationUrl) {
    try {
        const bgTask = yield fork(
            callWelcomePageCallbackAfterDelay,
            postCreateAccountRedirect,
            postAuthenticationUrl
        );

        const { source } = yield take(SignInSliderActionTypes.INITIATE_SIGNIN_SLIDER_CLOSE);
        yield cancel(bgTask);
        // not to redirect if cam nav is clicked
        if (source !== 'camnav') {
            // console.log('source', source, 'executing callback');
            yield fork(welcomePageCallBack, postCreateAccountRedirect, postAuthenticationUrl);
        }
    } catch (err) {
        console.error('some error occured:', err);
    }
}

export function* onCloseSlider() {
    try {
        yield put(initiateSignInSliderClose());
    } catch (error) {
        console.log('onCloseSlider error: ', error);
    }
}

export function* onAuthenticationSuccess(action, postCreateAccountRedirect, postAuthenticationUrl) {
    try {
        const hasJCPCard = TokenProvider.get(DP_HAS_JCP_CARD);
        const enablePlccBanner = yield select(selectPlccBannerThrottleFF);
        const confirmCreateAccountPage = get(action, 'payload.confirmCreateAccountPage');
        yield call(updateHeaderReduxState);
        if (hasJCPCard === 'true' && enablePlccBanner) {
            yield put({ type: PLCC_BANNER_REQUEST });
        }
        if (
            (!get(action, 'payload.isSliderCustomized') ||
                get(action, 'payload.skipOnAuthenticationSuccess')) &&
            !confirmCreateAccountPage
        ) {
            const featureFlags = yield select(selectFeatureFlags);
            const isPhoneUnVerified =
                once(() => TokenProvider.get('DP_PHONE_VERIFIED_STATUS') || 'false')() === 'false';
            const isMobileType =
                once(() => TokenProvider.get('DP_PHONE_NUMBER_TYPE') || '')().toLowerCase() ===
                'mobile';
            const wasInRecognisedState =
                TokenProvider.get('ANALY_CUST_STATE') === 'recognized|logged-in';
            if (wasInRecognisedState || get(action, 'payload.isHideAccountMenu')) {
                // lets not wait for anything.
                // it was recognized state and user is being asked to step up
                // so lets call callback as soon as possible
                yield fork(welcomePageCallBack, postCreateAccountRedirect, postAuthenticationUrl);
            } else if (
                get(featureFlags, 'disableVerificationOnSlider') ||
                !isPhoneUnVerified ||
                !isMobileType ||
                !get(featureFlags, 'enablePhoneLogin')
            ) {
                yield fork(
                    delayedCallWelcomePageTask,
                    postCreateAccountRedirect,
                    postAuthenticationUrl
                );
            } else {
                // with verification flow enabled
                // on success of verification delayed callback will be executed
                // or when user stops verification , callback will be executed immediately
                yield put(
                    startPhoneVerification('REQUEST_OTP', {
                        onPhoneVerificationStop: function* onPhoneVerificationStop() {
                            yield fork(
                                welcomePageCallBack,
                                postCreateAccountRedirect,
                                postAuthenticationUrl
                            );
                        },
                    })
                );
            }
        } else {
            // if lnlPage, make a customconfig callback and handle onAuthenticationSuccess
            const islnlPage = get(action, 'payload.lnlPage');
            !islnlPage && !confirmCreateAccountPage && (yield call(onCloseSlider));
        }
    } catch (error) {
        console.log('onAuthenticationSuccess error: ', error);
    }
}

function openSignInPage() {
    try {
        window.location.assign('/signin');
    } catch (error) {
        console.log('openSignInPage error: ', error);
    }
}

function openCreateAccountPage() {
    try {
        window.location.assign('/createaccount');
    } catch (error) {
        console.log('openCreateAccountPage error: ', error);
    }
}

function openLogoutPage() {
    try {
        /* clear DPOrder and AccountID to avoid
        accountrelated call from header in sessionTimoutpage */
        setValue('ACCOUNT_ID', '', true);
        Cookies.remove('DPOrder');
        LocalStorage.removeData('DP_REWARDS_DETAILS');
        LocalStorage.removeData('DP_REWARDS_ID');
        LocalStorage.removeData('qsEligibilityData');
        window.location.assign('/sessiontimeout');
    } catch (error) {
        console.log('openLogoutPage error: ', error);
    }
}

function* openActualPage(signin, createaccount, isLogout) {
    try {
        if (signin && !createaccount && !isLogout) {
            yield fork(openSignInPage);
        } else if (createaccount) {
            yield fork(openCreateAccountPage);
        } else if (isLogout) {
            yield fork(openLogoutPage);
        }
    } catch (error) {
        console.log('openActualPage error:', error);
    }
}

export function* openSignInSlider(action) {
    try {
        const preferences = yield select(selectPreferences);
        const currentPage =
            !__SERVER__ && window.location.pathname ? window.location.pathname : undefined;
        const postCreateAccountRedirect = yield call(getPostCreateAccountRedirect);
        const postAuthenticationUrl = yield call(getPostAuthenticationUrl);
        // 1. check for negative cases viz flags
        // 2. otherwise if logout, lets cleanup and update header no need to open slider
        // 3. if create account, check for postCreateAccountRedirect
        if (get(preferences, 'excludeSignInSliderRoutes', []).includes(currentPage)) {
            const signInSliderDetails = yield select(getSignInSliderDetails);
            const {
                mountSignInSlider: openSignIn,
                openCreateAccount,
                openLogout,
            } = signInSliderDetails;
            yield* openActualPage(openSignIn, openCreateAccount, openLogout);
        } else {
            yield put(signInSliderMounted());
        }
        const { type } = yield take([
            SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS,
            SignInSliderActionTypes.CUSTOM_STEPUP_SIGNIN_SLIDER_SKIP,
        ]);
        switch (type) {
            case SignInSliderActionTypes.SIGNIN_SLIDER_POST_SIGNIN_SUCCESS: {
                yield* onAuthenticationSuccess(
                    action,
                    postCreateAccountRedirect,
                    postAuthenticationUrl
                );
                break;
            }

            case SignInSliderActionTypes.CUSTOM_STEPUP_SIGNIN_SLIDER_SKIP:
                yield* onCloseSlider();
                break;
            default:
                console.error('missing implementation', type);
        }
    } catch (error) {
        console.error('openSignInSlider error: ', error);
    }
}

export function* createAccountSuccessSaga(action) {
    try {
        const postCreateAccountRedirect = yield call(getPostCreateAccountRedirect);
        const postAuthenticationUrl = yield call(getPostAuthenticationUrl);
        yield put(accountRegisteredAction());
        const actions = { ...action };
        actions.payload.confirmCreateAccountPage = true;
        const analyticsData = {
            trackEvent: 'linkRewardsAccount',
            pageName: 'create account group-a',
        };
        yield put(rewardsMigrationAction(analyticsData));
        yield* onAuthenticationSuccess(actions, postCreateAccountRedirect, postAuthenticationUrl);
    } catch (error) {
        console.error('CREATE ACCOUNT SUCCESS SAGA: ', error);
    }
}

export function* nativeCreateAccountSuccessSaga(action) {
    const isCreateAccount = action?.payload?.isCreateAccount;
    if (isCreateAccount) {
        yield put({ type: SignInSliderActionTypes.NATIVE_SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS });
    }
}

function* hideWelcomeTooltipOnDelay() {
    try {
        const preferences = yield select(selectPreferences);
        const signInSliderWelcomeMessageTimeOut = get(
            preferences,
            'signInSliderWelcomeMessageTimeOut',
            3000
        );
        const parsedNumber = Number(signInSliderWelcomeMessageTimeOut);
        const DELAY = parsedNumber || 3000;
        yield delay(DELAY);
        yield put(hideWelcomeToolTip());
        yield delay(10);
        yield put(resetSignInSlider());
    } catch (error) {
        console.log('hideWelcomeTooltipOnDelay error: ', error);
    }
}

function getBBId() {
    return !__SERVER__ && get(invoke(window, 'IGLOO.getBlackbox'), 'blackbox');
}

function reloadCurrentLocation() {
    window.location.reload();
}

export function* logoutApiSaga(action = {}) {
    try {
        const { payload: { callback = noop } = {} } = action;
        const featureFlags = yield select(selectFeatureFlags);
        // Logout call based on feature flag
        if (!featureFlags.enableLogoutApiCall) {
            callback();
            return;
        }

        const refreshToken = TokenProvider.get(REFRESH_TOKEN);
        if (!refreshToken) {
            callback();
            return;
        }
        // Keep me loggedIn Logic in logout Api
        let bbId;
        const enableKeepMeLoggedIn = yield select(selectKeepMeLoggedInThrottleFF);
        const keepMeLoggedInSelectedInSession = TokenProvider.get(DP_KEEP_ME_LOGGED) === '1';
        if (enableKeepMeLoggedIn && User.isUserLoggedIn(true) && keepMeLoggedInSelectedInSession) {
            bbId = getBBId();
            if (!bbId) {
                console.warn('Iovation Script is loaded yet...');
                const preferences = yield select(selectPreferences);
                const { type } = yield race([
                    takeMaybe([IOVATION_SCRIPT_LOAD_ERROR, IOVATION_SCRIPT_LOAD_SUCCESS]),
                    // this race condition added so no call blocks
                    delay(get(preferences, 'iovationScriptLoadTimeOut', 2000)),
                ]);
                if (!type || type === IOVATION_SCRIPT_LOAD_ERROR) {
                    console.warn('Iovation script failed or timed out: -> ', type);
                } else {
                    bbId = getBBId();
                }
            }
        }

        const apiAction = {
            params: {}, // empty as per contract
            customHeaders: {
                REFRESH_TOKEN: refreshToken,
                ...(bbId && { 'X-BLACKBOX': bbId }),
            },
        };
        // should be async as saw some delay in updating some stuff
        // eslint-disable-next-line func-names
        yield fork(function* () {
            const response = yield call(logout, apiAction);
            if (![202, 200].includes(response.status)) {
                console.warn('LOGOUT API FAILED', response.data, response.error);
            } else {
                LocalStorage.removeData('DP_REWARDS_DETAILS');
                LocalStorage.removeData('DP_REWARDS_ID');
            }
        });
        callback();
    } catch (error) {
        console.error('EXCEPTION LOGOUT API SAGA: ', error);
    }
}

export function* logoutSaga() {
    try {
        const preferences = yield select(selectPreferences);
        const featureFlags = yield select(selectFeatureFlags);
        const { enableQuickScreenBanner = true } = yield select(selectFeatureFlags) || {};
        const currentPage =
            !__SERVER__ && window.location.pathname ? window.location.pathname : undefined;
        if (!featureFlags.enableLogoutSlider) {
            yield fork(openLogoutPage);
        } else {
            // blocking as call should be made before any redirects/clearing from browser etc
            yield call(logoutApiSaga);
            TokenProvider.logout();
            yield put({ type: CLEAR_PLCC_BANNER_DETAILS });
            if (enableQuickScreenBanner) {
                yield put({ type: RESET_QUICKSCREEN_ELIGIBLITY });
            }
            yield call(updateHeaderReduxState);
            // scroll to top so that user sees the tooltip
            !__SERVER__ &&
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            const location = get(preferences, `postLogoutRedirects[${currentPage}]`, '');
            if (!__SERVER__ && location) {
                // in this no need of hiding tooltip
                window.location.assign(location);
            } else {
                const bgTask = yield fork(hideWelcomeTooltipOnDelay);
                const requestChan = yield actionChannel([
                    SignInSliderActionTypes.SIGNIN_SLIDER_OPEN,
                ]);
                while (true) {
                    // NOSONAR
                    yield take(requestChan);
                    yield cancel(bgTask);
                }
            }
        }
    } catch (error) {
        console.error('Logout saga: ', error);
    }
}

function* watchSignInSaga() {
    yield takeLatest(SignInActionTypes.POST_SIGNIN_DETAILS, signInSaga);
    yield takeLatest(SignInActionTypes.SAVED_ITEMS_MERGE, savedItemMerge);
    yield takeLatest(SignInSliderActionTypes.CUSTOM_STEPUP_SIGNIN_SLIDER, customStepUpSaga);
    yield takeLatest(SignInSliderActionTypes.SIGNIN_SLIDER_SHOW_LOGOUT_TOOL_TIP, logoutSaga);
    yield takeLatest(SignInSliderActionTypes.SIGNIN_SLIDER_OPEN, openSignInSlider);
    yield takeLatest(
        SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS,
        createAccountSuccessSaga
    );
    yield takeLatest(
        SignInSliderActionTypes.NATIVE_SIGNIN_SLIDER_CREATE_ACCOUNT,
        nativeCreateAccountSuccessSaga
    );
    yield takeLatest(SignInSliderActionTypes.SIGNIN_SLIDER_LOGOUT_API_REQUEST, logoutApiSaga);
    yield takeLatest(GLOBAL_LOGOUT, logoutApiSaga);
    yield takeLatest(SignInActionTypes.REFRESH_HEADER, updateHeaderReduxState);
    yield takeLatest(SignInActionTypes.RELOAD_LOCATION, reloadCurrentLocation);
}

watchSignInSaga.sagaName = 'signInSaga';
export default watchSignInSaga;
